import type { ReactNode } from 'react';
import {
  LEGAL_CONTACT_EMAIL,
  LEGAL_DATA_CONTROLLER,
  LEGAL_LAST_UPDATED,
  LEGAL_SITE_URL,
} from '../data/legal';

interface LegalPageProps {
  onBack: () => void;
}

export default function LegalPage({ onBack }: LegalPageProps) {
  return (
    <div className="mx-auto max-w-2xl space-y-8 pb-8 text-sm text-muted">
      <header className="space-y-3">
        <h1 className="text-2xl font-semibold text-strong">Privatliv og vilkår</h1>
        <p className="text-faint">Sidst opdateret {LEGAL_LAST_UPDATED}</p>
        <nav aria-label="Indhold" className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <a href="#privatliv" className="text-indigo-600 hover:underline dark:text-indigo-400">
            Privatlivspolitik
          </a>
          <a href="#vilkaar" className="text-indigo-600 hover:underline dark:text-indigo-400">
            Vilkår for brug
          </a>
          <a href="#kontakt" className="text-indigo-600 hover:underline dark:text-indigo-400">
            Kontakt
          </a>
        </nav>
      </header>

      <section id="privatliv" className="space-y-4 scroll-mt-6">
        <h2 className="text-lg font-semibold text-strong">Privatlivspolitik</h2>

        <LegalBlock title="Dataansvarlig">
          <p>
            {LEGAL_DATA_CONTROLLER} er dataansvarlig for behandling af dine
            personoplysninger i TouchType ({LEGAL_SITE_URL}).
          </p>
        </LegalBlock>

        <LegalBlock title="Hvilke data vi behandler">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Øveprofil:</strong> navn (valgfrit kaldenavn), tastaturlayout,
              trin, XP, streak, fejlstatistik, lektionsresultater, badges,
              indstillinger og benchmarks.
            </li>
            <li>
              <strong>Konto (valgfrit):</strong> e-mailadresse og krypteret
              adgangskode-hash.
            </li>
            <li>
              <strong>Teknisk:</strong> en nødvendig session-cookie til at genkende
              din browser og synkronisere data.
            </li>
            <li>
              <strong>AI-øvelser (valgfrit):</strong> hvis du bruger
              fortællings- eller chat-øvelser, kan tekst du skriver sendes til
              vores server og videre til en AI-udbyder (OpenAI) for at generere
              svar. Brug ikke personlige eller følsomme oplysninger i disse
              øvelser.
            </li>
          </ul>
        </LegalBlock>

        <LegalBlock title="Formål">
          <p>
            Vi behandler data for at drive touchtyping-træneren, gemme din
            fremgang, synkronisere på tværs af enheder når du har konto, og —
            hvis du vælger det — levere AI-baserede øvelser.
          </p>
        </LegalBlock>

        <LegalBlock title="Retsgrundlag">
          <p>
            Behandlingen sker på baggrund af samtykke (oprettelse af profil/konto)
            og vores legitime interesse i at levere og forbedre tjenesten. Du kan
            bruge appen uden konto; konto er frivillig.
          </p>
        </LegalBlock>

        <LegalBlock title="Cookies">
          <p>
            TouchType sætter en <strong>nødvendig session-cookie</strong> (
            <code className="rounded bg-zinc-100 px-1 py-0.5 text-xs dark:bg-zinc-800">
              touchtype_session
            </code>
            ) så din profil og login kan genkendes. Cookien er httpOnly og
            bruges ikke til reklamer eller sporing på tværs af andre websites.
          </p>
        </LegalBlock>

        <LegalBlock title="Opbevaring og hosting">
          <p>
            Data opbevares på server i EU/Danmark. Vi gemmer dine oplysninger så
            længe du har en aktiv profil eller konto. Inaktive anonyme sessioner
            kan slettes efter udløb af session-cookien.
          </p>
        </LegalBlock>

        <LegalBlock title="Deling med tredjeparter">
          <p>
            Vi sælger ikke dine data. AI-øvelser kan involvere OpenAI som
            databehandler, når funktionen kører i live-tilstand. Hosting og
            database kører på infrastruktur vi kontrollerer.
          </p>
        </LegalBlock>

        <LegalBlock title="Dine rettigheder">
          <p>Du har ret til at:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Få indsigt i hvilke data vi har om dig</li>
            <li>Få rettet urigtige oplysninger</li>
            <li>Få slettet din konto og tilknyttede data</li>
            <li>Trække samtykke tilbage (slet konto eller stop med at bruge appen)</li>
            <li>Klage til Datatilsynet (datatilsynet.dk)</li>
          </ul>
          <p className="mt-2">
            Du kan slette enkelte profiler under «Vælg bruger», eller slette
            hele din konto (med adgangskode) på samme skærm når du er logget ind.
            Kontakt os på{' '}
            <ContactEmail />
            hvis du har brug for hjælp.
          </p>
        </LegalBlock>
      </section>

      <section id="vilkaar" className="space-y-4 scroll-mt-6">
        <h2 className="text-lg font-semibold text-strong">Vilkår for brug</h2>

        <LegalBlock title="Tjenesten">
          <p>
            TouchType er en gratis browserbaseret træner til dansk
            ti-fingersystem (blindskrift). Tjenesten leveres som den er og kan
            ændres eller udvides over tid.
          </p>
        </LegalBlock>

        <LegalBlock title="Acceptabel brug">
          <p>Du må ikke:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Forsøge at omgå sikkerhed eller overbelaste serveren</li>
            <li>Dele login med andre på en måde der bryder andres privatliv</li>
            <li>Indsende ulovligt, stødende eller personfølsomt indhold i AI-øvelser</li>
          </ul>
        </LegalBlock>

        <LegalBlock title="Konto og profiler">
          <p>
            Du er ansvarlig for at holde din adgangskode hemmelig. Profiler uden
            konto er knyttet til din browser via cookie. Med konto kan du synce
            på tværs af enheder. Du kan bruge flere profiler på samme enhed (fx
            søskende).
          </p>
        </LegalBlock>

        <LegalBlock title="Intellektuel ejendom">
          <p>
            TouchType, curriculum, tekster og design tilhører
            {` ${LEGAL_DATA_CONTROLLER}`}. Du må bruge tjenesten til personlig
            læring og undervisning, men ikke kopiere eller videresælge
            indholdet uden aftale.
          </p>
        </LegalBlock>

        <LegalBlock title="Ansvarsfraskrivelse">
          <p>
            Vi bestræber os på stabil drift, men garanterer ikke uafbrudt adgang.
            TouchType erstatter ikke formel undervisning. AI-genereret indhold i
            eksperimentelle øvelser kan indeholde fejl — brug sund fornuft.
          </p>
        </LegalBlock>

        <LegalBlock title="Ændringer">
          <p>
            Vi kan opdatere disse vilkår og privatlivspolitikken. Væsentlige
            ændringer vil fremgå af datoen øverst på denne side.
          </p>
        </LegalBlock>
      </section>

      <section id="kontakt" className="space-y-3 scroll-mt-6">
        <h2 className="text-lg font-semibold text-strong">Kontakt</h2>
        <p>
          Spørgsmål om privatliv, sletning af konto eller fejlrapporter:
        </p>
        <p>
          <ContactEmail />
        </p>
      </section>

      <button type="button" onClick={onBack} className="btn-secondary">
        Tilbage
      </button>
    </div>
  );
}

function LegalBlock({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <h3 className="font-medium text-strong">{title}</h3>
      {children}
    </div>
  );
}

function ContactEmail() {
  return (
    <a
      href={`mailto:${LEGAL_CONTACT_EMAIL}`}
      className="text-indigo-600 hover:underline dark:text-indigo-400"
    >
      {LEGAL_CONTACT_EMAIL}
    </a>
  );
}
